import { useCallback, useState } from 'react';
import { requestTyped } from '@xdocs/ws-protocol';
import { errorAlert } from '@xdocs/schemas';
import type { RenderFormat } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

export interface UseExportResult {
  jobId: string | null;
  isStarting: boolean;
  startError: string | null;
  /** F67 §28.4: `export.start` returns a `jobId` fast — live progress is the
   * caller's job (reuse `features/jobs`' `JobProgressCard`/`useJobSnapshot`
   * on the returned id), never blocking here on the render itself. */
  start: (format: RenderFormat) => void;
  reset: () => void;
}

/**
 * F67 export (§28.4): `document.export`-adjacent op `export.start` enqueues
 * the existing `render-worker` (no second PDF/print engine, §13). The
 * artifact never rides the WS payload (§5.7/§5.8) — only the `jobId` here;
 * resolving the eventual presigned download link is the caller's job once
 * the tracked job reaches `succeeded` (see `ExportMenu.tsx`).
 */
export function useExport(documentId: string, versionId: string): UseExportResult {
  const { wsClient } = useWs();
  const [jobId, setJobId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const start = useCallback(
    (format: RenderFormat) => {
      setIsStarting(true);
      setStartError(null);
      setJobId(null);
      requestTyped(wsClient, 'export.start', { documentId, versionId, format })
        .then((accepted) => setJobId(accepted.jobId))
        .catch((err: unknown) => {
          const parsed = errorAlert.safeParse(err);
          setStartError(parsed.success ? parsed.data.message : 'errors:code.INTERNAL');
        })
        .finally(() => setIsStarting(false));
    },
    [wsClient, documentId, versionId],
  );

  const reset = useCallback(() => {
    setJobId(null);
    setStartError(null);
  }, []);

  return { jobId, isStarting, startError, start, reset };
}
